import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { TooltipOptions } from 'marketpro-design-lib';
import * as condition from 'ol/events/condition';
import Feature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import Geometry from 'ol/geom/Geometry';
import Select from 'ol/interaction/Select';
import BaseLayer from 'ol/layer/Base';
import VectorImageLayer from 'ol/layer/VectorImage';
import { default as OlMap } from 'ol/Map';
import Overlay from 'ol/Overlay';
import * as olProj from 'ol/proj';
import RenderFeature from 'ol/render/Feature';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import View from 'ol/View';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../auth/services/auth.service';
import { GroupedResponse } from '../../../models/grouped-statistics-response.model';
import { SimpleStatisticsResponse } from '../../../models/simple-statistics-response.model';
import { DataService } from '../../../services/data.service';
import { constants } from './../../../../../constants';
import { Permissions } from '../../../../auth/models/permissions.model';

@Component({
  selector: 'mpro-export-data-map',
  templateUrl: './export-data-map.component.html',
  styleUrls: ['./export-data-map.component.scss'],
})
export class ExportDataMapComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  public map: OlMap;
  public worldMap: BaseLayer;
  private current: GroupedResponse<SimpleStatisticsResponse>;
  private ngUnsubscribe: Subject<void> = new Subject();
  private features;

  @Output() public countrySelected: EventEmitter<string> = new EventEmitter();
  @Output() public countryDeselected: EventEmitter<string> = new EventEmitter();

  @ViewChild('tooltip')
  public tooltip: ElementRef;

  public mapTooltip: TooltipOptions = {
    type: 'simple',
    title: '',
  };

  public selectedCountries: Set<string> = new Set();

  private popupOverlay: Overlay;

  private permissions: Permissions;
  private isWorldSelected: boolean = true;

  constructor(
    private dataService: DataService,
    private http: HttpClient,
    private authService: AuthService
  ) {
    this.http.get(constants.uriParts.features).subscribe((features) => {
      this.features = features;
      this.initMap();
    });
  }

  public ngOnInit(): void {
    this.authService.permissions$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((permissions) => (this.permissions = permissions));
    this.dataService
      .getData()
      .pipe(takeUntil(this.ngUnsubscribe), debounceTime(300))
      .subscribe((data) => {
        if (data.countryData.current && data.countryData.total) {
          this.current = data.countryData.current[constants.initialYear];
        }
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public ngAfterViewInit(): void {}

  public selectMap() {
    if (!this.features?.features) {
      return;
    }
    this.isWorldSelected = true;
    this.features.features.forEach((feature) => {
      const countryCode = feature.properties.adm0_a3;
      if (
        !this.permissions.allowedCountries?.permissionExtended?.length ||
        this.permissions.allowedCountries?.permissionExtended?.includes(
          countryCode
        )
      ) {
        this.selectedCountries.add(countryCode);
      }
    });
  }

  private initMap() {
    this.selectMap();
    let vectorSource = new VectorSource({
      features: new GeoJSON().readFeatures(this.features, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      }),
    });

    this.worldMap = new VectorImageLayer({
      imageRatio: 2,
      source: vectorSource,
      style: (f) => {
        return this.getStyle(f);
      },
    });

    this.map = new OlMap({
      target: 'export-data-map',
      layers: [this.worldMap],
      view: new View({
        center: olProj.fromLonLat([0, 18]),
        zoom: 2,
        minZoom: 2,
        maxZoom: 19,
      }),
    });

    this.popupOverlay = new Overlay({
      element: this.tooltip.nativeElement,
      offset: [9, 9],
    });
    this.map.addOverlay(this.popupOverlay);

    this.map.on('pointermove', (event) => {
      if (event.dragging) {
        return false;
      }

      if (!this.map.getFeaturesAtPixel(event.pixel).length) {
        this.tooltip.nativeElement.hidden = true;
        this.map.getViewport().style.cursor = '';
        return;
      }
      this.map.forEachFeatureAtPixel(event.pixel, (f, layer) => {
        const countryCode = f.get('adm0_a3');
        if (!this.current || !this.current[countryCode]) {
          return true;
        }
        this.map.getViewport().style.cursor = 'pointer';

        this.setCountryTooltipData(f);
        this.popupOverlay.setPosition(event.coordinate);

        return true;
      });

      return false;
    });

    // set color of country on hover
    var select = new Select({
      condition: condition.pointerMove,
      style: (feature: any) => {
        return new Style({
          fill: new Fill({
            color: '#E3EBFF',
          }),
          stroke: new Stroke({
            color: 'black',
            width: 1,
          }),
        });
      },
    });

    this.map.addInteraction(select);

    this.map.getViewport().addEventListener(
      'mouseout',
      (evt) => {
        this.tooltip.nativeElement.hidden = true;
      },
      false
    );

    this.map.on('click', (event) => {
      if (!this.map.getFeaturesAtPixel(event.pixel).length) {
        this.tooltip.nativeElement.hidden = true;
        return;
      }
      this.map.forEachFeatureAtPixel(event.pixel, (f, layer) => {
        let countryCode = f.get('adm0_a3');
        if (!this.current || !this.current[countryCode]) {
          return true;
        }
        if (!this.isWorldSelected) {
          if (this.selectedCountries.has(countryCode)) {
            this.selectedCountries.delete(countryCode);
            if (!this.selectedCountries.size) {
              this.selectMap();
            }
            this.countryDeselected.emit(countryCode);
          } else {
            this.selectedCountries.add(countryCode);
            this.countrySelected.emit(countryCode);
          }
        } else {
          this.isWorldSelected = false;
          this.selectedCountries.clear();
          this.selectedCountries.add(countryCode);
          this.countrySelected.emit(countryCode);
        }
        (<any>f).changed();
        return true;
      });
    });
  }

  private setCountryTooltipData(f: RenderFeature | Feature<Geometry>) {
    let countryCode = f.get('adm0_a3');

    if (!countryCode) {
      this.tooltip.nativeElement.hidden = true;
      return;
    }

    this.mapTooltip.title = f.get('admin');
    this.tooltip.nativeElement.hidden = false;
  }

  private getStyle(f: RenderFeature | Feature<Geometry>): Style | undefined {
    const countryCode = f.get('adm0_a3'); // export data map only works with countries

    var style = new Style({
      fill: new Fill({
        color: !this.selectedCountries.has(countryCode) ? '#F7F7F7' : '#B5CBFF',
      }),
      stroke: new Stroke({
        color: 'black',
        width: 1,
      }),
    });

    return style;
  }
}
